import type { Grid } from "./Grid.ts";
import { CellType } from "./types.ts";

/**
 * BL-147 — the ONE spawn model.
 *
 * The spawn system is two channels (the static perimeter channel in
 * `createGrid` and the dynamic breach channel in `BreachSystem`) sharing one
 * multi-path spine. Before this, the layout shape was declared THREE times
 * (createGrid `SpawnPosition`/`SpawnLayout`, BreachSystem `SpawnLayoutInit`,
 * SpawnDetectionSystem `HiddenSpawnLayout`) and the grid-rescan fallback TWICE
 * (BreachSystem.init + SpawnDetectionSystem.init). This module is the single
 * source for both — a pure-data seam in `core` (no DOM, imports only sideways:
 * `Grid` as a type, `CellType` as the cell enum), so it sits below every
 * consumer and the layer-direction lock stays green.
 */

/** One spawn-origin cell. The single `{ col, row }` position shape shared by
 *  the static layout, the breach registry, and detection. */
export interface SpawnCell {
  col: number;
  row: number;
}

/**
 * The run's spawn layout — the dormant∶hidden split chosen at grid-build time.
 * Both kinds erupt mid-run (BreachSystem); the `hidden` kind additionally
 * demands a Signal tower to reveal early (SpawnDetectionSystem). The label is a
 * telegraph axis, not an eruption axis (S149 §6.3). This is the ONE layout
 * shape consumed by createGrid, BreachSystem, and SpawnDetectionSystem.
 */
export interface SpawnLayout {
  dormant: SpawnCell[];
  hidden: SpawnCell[];
}

/** Deep-clone a layout so callers can't mutate the live registry (createGrid
 *  hands out copies via `getSpawnLayout`). */
export function cloneSpawnLayout(layout: SpawnLayout): SpawnLayout {
  return {
    dormant: layout.dormant.map((c) => ({ ...c })),
    hidden: layout.hidden.map((c) => ({ ...c })),
  };
}

/**
 * Row-major scan of the grid for spawn-origin cells — the SINGLE grid-rescan
 * fallback, formerly duplicated in `BreachSystem.init` and
 * `SpawnDetectionSystem.init`. Used only when no run layout is supplied
 * (production always passes `getSpawnLayout()`); the fallback now produces the
 * same dormant-then-hidden ordering the layout path uses, so the two init
 * paths agree.
 */
export function scanSpawnLayout(grid: Grid): SpawnLayout {
  const dormant: SpawnCell[] = [];
  const hidden: SpawnCell[] = [];
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      const cell = grid.getCell(col, row);
      if (cell?.type === CellType.DORMANT_SPAWN) {
        dormant.push({ col, row });
      } else if (cell?.type === CellType.HIDDEN_SPAWN) {
        hidden.push({ col, row });
      }
    }
  }
  return { dormant, hidden };
}
